import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  X,
  Upload,
  UploadCloud,
  File,
  Image as ImageIcon,
  Film,
  FileText,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';
import ChatModalPortal from './ChatModalPortal';

export function formatFileSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function getFileIcon(file) {
  const type = file?.type || '';
  const name = file?.name || '';
  if (type.startsWith('image/')) return ImageIcon;
  if (type.startsWith('video/')) return Film;
  if (type.includes('pdf') || name.endsWith('.pdf')) return FileText;
  return File;
}

export function getFileType(file) {
  const type = file?.type || '';
  const name = file?.name || '';
  if (type.startsWith('image/')) return type.split('/')[1]?.toUpperCase() || 'IMG';
  if (type.startsWith('video/')) return type.split('/')[1]?.toUpperCase() || 'VID';
  if (type.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  return 'DOK';
}

// Client-side canvas compression matching entriks-hr
export const compressImage = (file) => {
  return new Promise((resolve) => {
    if (!file?.type?.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (event) => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxDim = 1200;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
              resolve(
                new File([blob], `${baseName}.jpg`, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                })
              );
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
};

function FileUploadCard({ fileWrapper, preview, onRemove }) {
  const FileIcon = getFileIcon(fileWrapper.file);
  const isImage = fileWrapper.file?.type?.startsWith('image/');

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/50 p-2.5 transition-all hover:bg-zinc-50">
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-zinc-100 flex items-center justify-center border border-zinc-200">
        {preview && isImage ? (
          <img src={preview} alt="" className="h-full w-full object-cover" />
        ) : (
          <FileIcon className="h-6 w-6 text-zinc-500" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-bold text-zinc-900">{fileWrapper.name}</p>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-500 font-medium">
          <span>{getFileType(fileWrapper.file)}</span>
          <span>•</span>
          <span>{formatFileSize(fileWrapper.size)}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onRemove(fileWrapper.id)}
        className="shrink-0 rounded-xl p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600 transition"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export const FileUploadModal = ({
  open,
  onClose,
  onSendFiles,
}) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({});
  const [caption, setCaption] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [compressImageEnabled, setCompressImageEnabled] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);
  const previewsRef = useRef(previews);

  useEffect(() => {
    previewsRef.current = previews;
  }, [previews]);

  useEffect(() => {
    if (!open) {
      Object.values(previewsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      setFiles([]);
      setPreviews({});
      setCaption('');
      setIsProcessing(false);
    }
  }, [open]);

  const addFiles = useCallback((incomingFiles) => {
    if (!incomingFiles || incomingFiles.length === 0) return;
    const wrappers = Array.from(incomingFiles).map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));

    setFiles((prev) => [...prev, ...wrappers]);

    const newPreviews = {};
    wrappers.forEach((w) => {
      if (w.file?.type?.startsWith('image/')) {
        newPreviews[w.id] = URL.createObjectURL(w.file);
      }
    });
    setPreviews((prev) => ({ ...prev, ...newPreviews }));
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    if (previews[id]) {
      URL.revokeObjectURL(previews[id]);
      setPreviews((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const handleSend = async () => {
    if (files.length === 0) return;
    setIsProcessing(true);
    try {
      const processedFiles = await Promise.all(
        files.map(async (w) => {
          if (w.file.type.startsWith('image/') && compressImageEnabled) {
            const compressed = await compressImage(w.file);
            return {
              file: compressed,
              name: compressed.name,
              preview: previews[w.id] || null,
            };
          }
          return {
            file: w.file,
            name: w.name,
            preview: previews[w.id] || null,
          };
        })
      );

      if (onSendFiles) {
        onSendFiles(processedFiles, caption.trim());
      }
      onClose();
    } catch (err) {
      console.error('File send error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!open) return null;

  const hasFiles = files.length > 0;
  const hasImages = files.some((f) => f.file?.type?.startsWith('image/'));

  return (
    <ChatModalPortal>
      <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isProcessing) onClose();
      }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border-0 bg-white shadow-2xl flex flex-col max-h-[85vh]">
        <div aria-hidden="true" className="flex h-1.5 w-full shrink-0">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4 bg-zinc-50/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-950 text-[#FFCE00] shadow-xs">
              <Upload size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold text-zinc-950">Ngarko Skedarë</h3>
              <p className="text-xs text-zinc-500">Imazhe, dokumente, PDF ose detyra gjermanisht</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 transition"
            aria-label="Mbyll dritaren"
            title="Mbyll"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all cursor-pointer',
              isDragging
                ? 'border-amber-400 bg-amber-50/50'
                : 'border-zinc-200 bg-zinc-50/40 hover:border-zinc-400 hover:bg-zinc-50'
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
              }}
            />
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-xs border border-zinc-200 mb-3 text-zinc-800">
              <UploadCloud size={28} />
            </div>
            <p className="text-xs font-bold text-zinc-900">
              Tërhiq skedarët këtu ose kliko për të shfletuar
            </p>
            <p className="text-[11px] text-zinc-400 mt-1">
              Mbështeten JPG, PNG, PDF, DOC, TXT (Deri në 50 MB)
            </p>
          </div>

          {/* Files List */}
          {hasFiles && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {files.map((w) => (
                <FileUploadCard
                  key={w.id}
                  fileWrapper={w}
                  preview={previews[w.id]}
                  onRemove={handleRemoveFile}
                />
              ))}
            </div>
          )}

          {/* Compress Image Checkbox (entriks-hr style) */}
          {hasImages && (
            <div className="flex items-center justify-between p-3 rounded-2xl border border-zinc-200 bg-zinc-50/50">
              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  id="compress-images-toggle"
                  checked={compressImageEnabled}
                  onChange={(e) => setCompressImageEnabled(e.target.checked)}
                  className="h-4.5 w-4.5 rounded-md accent-zinc-950 cursor-pointer"
                />
                <label
                  htmlFor="compress-images-toggle"
                  className="text-xs font-bold text-zinc-800 cursor-pointer select-none"
                >
                  Ngjesh imazhet (Compress images)
                </label>
              </div>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-full">
                ~70% më e lehtë
              </span>
            </div>
          )}

          {/* Caption */}
          {hasFiles && (
            <div>
              <label className="text-xs font-bold text-zinc-700 block mb-1.5">
                Përshkrim opsional (Caption)
              </label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={t('Shkruaj një shënim për këtë dokument...')}
                rows={2}
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950 resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {hasFiles && (
          <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/50 px-6 py-4">
            <span className="text-xs font-bold text-zinc-500">
              {files.length} {files.length === 1 ? 'skedar i zgjedhur' : 'skedarë të zgjedhur'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                className="rounded-xl text-xs font-bold"
              >
                Anulo
              </Button>
              <Button
                type="button"
                disabled={isProcessing}
                onClick={handleSend}
                className="rounded-xl bg-zinc-950 px-5 text-xs font-bold text-[#FFCE00] hover:bg-[#DD0000] hover:text-white"
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Duke ngjeshur...
                  </>
                ) : (
                  <>
                    <Upload size={14} className="mr-1.5" />
                    Dërgo Skedarët
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </ChatModalPortal>
  );
}

export default FileUploadModal;
